import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const SpecialNZScroll = ({ onSelectCountry }) => {
    const sectionRef = useRef(null);
    const imageRef = useRef(null);
    const contentRef = useRef(null);
    const featuresRef = useRef([]);

    const features = ['High visa success rate', 'Affordable education options', 'PR pathway guidance', 'Trusted by hundreds of students'];

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Image parallax with rotation effect
            gsap.fromTo(
                imageRef.current,
                {
                    opacity: 0,
                    x: 100,
                    scale: 0.9,
                    rotateY: 10,
                },
                {
                    opacity: 1,
                    x: 0,
                    scale: 1,
                    rotateY: 0,
                    duration: 1.2,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: 'top 80%',
                        end: 'top 40%',
                        scrub: 1,
                    },
                }
            );

            // Content slide in from left
            gsap.fromTo(
                contentRef.current,
                {
                    opacity: 0,
                    x: -100,
                },
                {
                    opacity: 1,
                    x: 0,
                    duration: 1.2,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: 'top 80%',
                        end: 'top 40%',
                        scrub: 1,
                    },
                }
            );

            // Features staggered reveal with bounce effect
            featuresRef.current.forEach((feature, index) => {
                gsap.fromTo(
                    feature,
                    {
                        opacity: 0,
                        y: 30,
                        scale: 0.8,
                    },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.8,
                        ease: 'back.out(1.7)',
                        scrollTrigger: {
                            trigger: feature,
                            start: 'top 90%',
                            end: 'top 60%',
                            scrub: 1,
                        },
                    }
                );
            });
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            ref={sectionRef}
            className="py-20 bg-lightGray relative overflow-hidden"
        >
            {/* Animated background pattern */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23E63946' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                }}></div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    {/* Image with parallax and rotation */}
                    <div ref={imageRef} className="relative order-2 lg:order-1">
                        <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                            <img
                                src="https://images.unsplash.com/photo-1507699622108-4be3abd695ad?w=600&q=80"
                                alt="New Zealand"
                                className="w-full h-auto transition-transform duration-700 hover:scale-105"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                        </div>
                        {/* Decorative elements */}
                        <div className="absolute -top-8 -right-8 w-32 h-32 bg-primary-red/10 rounded-full -z-10"></div>
                        <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-primary-red/10 rounded-full -z-10"></div>
                    </div>

                    {/* Content with kinetic typography */}
                    <div ref={contentRef} className="order-1 lg:order-2">
                        <h2 className="text-4xl font-bold text-black mb-6">
                            Best <span className="text-primary-red">New Zealand</span> Consultant in Hyderabad
                        </h2>
                        <p className="text-lg text-gray mb-6 leading-relaxed">
                            We have successfully guided hundreds of students to New Zealand with high visa success
                            and trusted support. Our expertise in New Zealand's education system and visa processes
                            makes us the preferred choice for students aspiring to study in this beautiful country.
                        </p>
                        <div className="flex flex-wrap gap-3 mb-8">
                            {features.map((feature, index) => (
                                <div
                                    key={index}
                                    ref={(el) => (featuresRef.current[index] = el)}
                                    className="flex items-center gap-2 bg-white px-5 py-2 rounded-full shadow-sm hover:shadow-md transition-shadow duration-300"
                                >
                                    <i className="fas fa-check-circle text-primary-red"></i>
                                    <span className="text-sm font-medium">{feature}</span>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={() => onSelectCountry && onSelectCountry('new-zealand')}
                            className="btn-primary inline-flex items-center gap-2"
                        >
                            <i className="fas fa-external-link-alt"></i>
                            Explore New Zealand
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SpecialNZScroll;
